import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import AddressAutocomplete from '../components/AddressAutocomplete';
import '../styles/Booking.css';

const bunnyIcon = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="%23FFB6C1"/><text x="50" y="65" font-size="60" text-anchor="middle">🐰</text></svg>`;

interface AppSummary {
  id: string;
  name: string;
  icon: string;
  hours: string;
  email: string;
  price: number;
  minAge?: number;
}

interface Child {
  name: string;
  age: string;
  gender: string;
  allergies: string;
  specialInstructions: string;
}

const Booking: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [app, setApp] = useState<AppSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [booked, setBooked] = useState(false);
  
  // Form State
  const [address, setAddress] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [startTime, setStartTime] = useState('04:00');
  const [startAmPm, setStartAmPm] = useState('PM');
  const [endTime, setEndTime] = useState('06:00');
  const [endAmPm, setEndAmPm] = useState('PM');
  const [numChildren, setNumChildren] = useState(1);
  const [children, setChildren] = useState<Child[]>([{ name: '', age: '', gender: '', allergies: '', specialInstructions: '' }]);
  const [validationError, setValidationError] = useState('');
  const [durationNote, setDurationNote] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/signup');
      return;
    }

    if (id === 'general') {
      setApp({
        id: 'general',
        name: 'a Sitter',
        icon: bunnyIcon,
        hours: 'General Availability',
        email: 'all-sitters@babysittingfind.ca',
        price: 15
      });
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/apps/${id}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) setApp(null);
        else setApp(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching sitter info:', err);
        setLoading(false);
      });
  }, [id, user, navigate]);

  const handleNumChildrenChange = (count: number) => {
    setNumChildren(count);
    const newChildren = [...children];
    if (count > children.length) {
      for (let i = children.length; i < count; i++) {
        newChildren.push({ name: '', age: '', gender: '', allergies: '', specialInstructions: '' });
      }
    } else {
      newChildren.splice(count);
    }
    setChildren(newChildren);
  };

  const updateChild = (index: number, field: keyof Child, value: string) => {
    const newChildren = [...children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setChildren(newChildren);
  };

  const getDayLimits = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    if (day >= 1 && day <= 4) return { start: "15:20", end: "21:00", label: '3:20 PM – 9:00 PM' };
    if (day === 5) return { start: "15:20", end: "21:30", label: '3:20 PM – 9:30 PM' };
    if (day === 6) return { start: "10:00", end: "21:30", label: '10:00 AM – 9:30 PM' };
    return { start: "10:00", end: "21:00", label: '10:00 AM – 9:00 PM' };
  };

  const toMinutes = (timeString: string, ampm?: string) => {
    let [hours, minutes] = timeString.split(':').map(Number);
    if (ampm) {
      if (ampm === 'PM' && hours !== 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
    }
    return hours * 60 + minutes;
  };

  useEffect(() => {
    let error = '';
    
    if (selectedDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const bookingDate = new Date(selectedDate);
      bookingDate.setHours(0, 0, 0, 0);
      
      const diffTime = bookingDate.getTime() - today.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (diffDays < 1) {
        error = 'Invalid date: You must provide at least 24 hours notice.';
      } else {
        const limits = getDayLimits(selectedDate);
        const limitStartMins = toMinutes(limits.start);
        const limitEndMins = toMinutes(limits.end);
        const startMins = toMinutes(startTime, startAmPm);
        const endMins = toMinutes(endTime, endAmPm);

        if (startMins < limitStartMins || startMins > limitEndMins || endMins < limitStartMins || endMins > limitEndMins) {
          error = `Invalid time: Selected time must be within ${limits.label}`;
        } else if (endMins <= startMins) {
          error = 'Invalid time: End time must be after start time.';
        } else if (endMins - startMins < 60) {
          error = 'Invalid time: Babysitting requires a 1-hour minimum.';
        }
      }

      const startMins = toMinutes(startTime, startAmPm);
      const endMins = toMinutes(endTime, endAmPm);
      const durationHours = (endMins - startMins) / 60;
      if (durationHours > 5) {
        setDurationNote('Note: For sessions over 5 hours, an extra fee applies.');
      } else {
        setDurationNote('');
      }
    }

    children.forEach((child, idx) => {
      if (child.age) {
        const age = parseInt(child.age);
        if (age < 1 || age > 10) {
          error = `Invalid age for Child #${idx + 1}: Must be between 1 and 10.`;
        } else if (app?.minAge && age < app.minAge) {
          error = `Invalid age for Child #${idx + 1}: ${app.name} requires at least ${app.minAge} years old.`;
        }
      }
    });

    setValidationError(error);
  }, [selectedDate, startTime, startAmPm, endTime, endAmPm, children, app]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validationError) return;

    const nsRegex = /(Nova Scotia|NS|B[0-9][A-Z] [0-9][A-Z][0-9])/i;
    if (!nsRegex.test(address)) {
      setValidationError('Invalid address: Please include "NS" or "Nova Scotia".');
      return;
    }

    const startMins = toMinutes(startTime, startAmPm);
    const endMins = toMinutes(endTime, endAmPm);
    const durationHours = (endMins - startMins) / 60;
    const basePrice = app?.price || 15;
    // $2 extra PER additional child
    const finalHourlyRate = basePrice + ((numChildren - 1) * 2);
    const totalPrice = durationHours * finalHourlyRate;

    const bookingData = {
      sitterId: id,
      sitterName: app?.name,
      sitterEmail: app?.email,
      isGeneral: id === 'general',
      parentName: user?.name,
      parentEmail: user?.email,
      parentPhone: user?.phone,
      emergencyPhone: emergencyPhone,
      address: address,
      date: selectedDate,
      startTime: `${startTime} ${startAmPm}`,
      endTime: `${endTime} ${endAmPm}`,
      numChildren: numChildren,
      children: children,
      totalPrice: totalPrice,
      hourlyRate: finalHourlyRate,
      status: 'Pending',
      timestamp: new Date()
    };

    fetch(`${API_URL}/api/bookings`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(bookingData),
    })
      .then(res => res.json())
      .then(() => {
        setBooked(true);
        setTimeout(() => { 
          if (id === 'general') navigate('/');
          else navigate(`/app/${id}`); 
        }, 3000);
      })
      .catch(err => {
        console.error('Error saving booking:', err);
        setValidationError('Failed to send booking request.');
      });
  };

  if (!user) return null;
  if (loading) return <div className="booking-container"><div className="loading-state">Loading...</div></div>;
  if (!app) return <div className="booking-container"><h2>Sitter not found</h2></div>;

  return (
    <div className="booking-container">
      {id === 'general' ? <Link to="/" className="back-link">← Back to Discover</Link> : <Link to={`/app/${id}`} className="back-link">← Back to Profile</Link>}
      <div className="booking-card">
        <div className="booking-header">
          <img src={app.icon} alt={app.name} className="booking-icon" />
          <h1>{id === 'general' ? 'Post a General Request' : `Book ${app.name}`}</h1>
        </div>

        {booked ? (
          <div className="success-msg">
            <h2>🎉 Request Sent!</h2>
            {id === 'general' ? (
              <p>Your request has been sent to all sitters in your area. You'll be notified when someone accepts!</p>
            ) : (
              <p>An email has been sent to {app.name}. They will confirm your booking shortly.</p>
            )}
          </div>
        ) : (
          <form className="booking-form" onSubmit={handleSubmit}>
            <div className="user-info-summary">
              <p>Booking as: <strong>{user.name}</strong> ({user.email})</p>
            </div>

            <div className="form-group">
              <label>Home Address</label>
              <AddressAutocomplete value={address} onChange={setAddress} placeholder="e.g. 123 Maple St, Apt 4" required />
            </div>

            <div className="form-group">
              <label>🚨 Emergency Contact Number</label>
              <input 
                type="tel" 
                placeholder="e.g. 902-555-5555" 
                required 
                value={emergencyPhone}
                onChange={(e) => setEmergencyPhone(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Date</label>
              <input name="date" type="date" required value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <div className="time-select-group">
                  <select value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <React.Fragment key={h}>
                        <option value={`${h.toString().padStart(2, '0')}:00`}>{h}:00</option>
                        <option value={`${h.toString().padStart(2, '0')}:30`}>{h}:30</option>
                      </React.Fragment>
                    ))}
                  </select>
                  <select value={startAmPm} onChange={(e) => setStartAmPm(e.target.value)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>End Time</label>
                <div className="time-select-group">
                  <select value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <React.Fragment key={h}>
                        <option value={`${h.toString().padStart(2, '0')}:00`}>{h}:00</option>
                        <option value={`${h.toString().padStart(2, '0')}:30`}>{h}:30</option>
                      </React.Fragment>
                    ))}
                  </select>
                  <select value={endAmPm} onChange={(e) => setEndAmPm(e.target.value)}>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            {validationError && <div className="auth-error">{validationError}</div>}

            <div className="form-group">
              <label>Number of Children ($2 extra per hour per additional child)</label>
              <select value={numChildren} onChange={(e) => handleNumChildrenChange(parseInt(e.target.value))}>
                {[1, 2, 3, 4].map(n => (
                  <option key={n} value={n}>{n} Child{n > 1 ? 'ren' : ''} (${(app?.price || 15) + ((n - 1) * 2)}/hr)</option>
                ))}
              </select>
            </div>

            {children.map((child, index) => (
              <div key={index} style={{ padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '12px', marginBottom: '15px', border: '1px solid #ddd' }}>
                <h3 style={{ marginTop: 0, fontSize: '1.1rem' }}>Child #{index + 1}</h3>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" required value={child.name} onChange={(e) => updateChild(index, 'name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Age</label>
                  <input type="number" required min="1" max="10" value={child.age} onChange={(e) => updateChild(index, 'age', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select required value={child.gender} onChange={(e) => updateChild(index, 'gender', e.target.value)}>
                    <option value="">Select Gender</option>
                    <option value="Boy">Boy</option>
                    <option value="Girl">Girl</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Allergies</label>
                  <textarea value={child.allergies} onChange={(e) => updateChild(index, 'allergies', e.target.value)} placeholder="None" />
                </div>
                <div className="form-group">
                  <label>Special Instructions</label>
                  <textarea value={child.specialInstructions} onChange={(e) => updateChild(index, 'specialInstructions', e.target.value)} placeholder="None" />
                </div>
              </div>
            ))}

            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#e3f2fd', 
              borderRadius: '15px', 
              border: '2px solid var(--primary-color)',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>Estimated Total ({((toMinutes(endTime, endAmPm) - toMinutes(startTime, startAmPm)) / 60).toFixed(1)} hours)</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                ${Math.max(0, (toMinutes(endTime, endAmPm) - toMinutes(startTime, startAmPm)) / 60 * ((app?.price || 15) + ((numChildren - 1) * 2))).toFixed(2)}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                Rate: ${(app?.price || 15) + ((numChildren - 1) * 2)}/hr (CASH ONLY)
              </div>
            </div>

            <button type="submit" className="book-submit-btn" disabled={!!validationError}>
              Send Booking Request
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Booking;
